import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { YMark } from "@/components/site/Logo";
import { MaskLines } from "@/components/site/Motion";

const HERO_SLIDES = [
  ["lisa-d", "05", "46% 30%"], ["nasty-b", "15", "45% 40%"], ["bianca-b", "16", "64% 30%"],
  ["dana-m", "19", "66% 20%"], ["beatrice", "05", "40% 30%"], ["teagan-b", "09", "38% 30%"],
];

const BIG_LINKS = [
  { to: "/models", label: "The Board" },
  { to: "/apply", label: "Get Scouted" },
  { to: "/contact", label: "Contact" },
];

const SideNav = () => (
  <nav className="hidden flex-col gap-1.5 md:flex" data-testid="hero-side-nav">
    {[["/models", "Models"], ["/apply", "Youth is yours"], ["/contact", "Contact"]].map(([to, l]) => (
      <Link key={to} to={to} className="link-underline link-light w-fit font-mono text-xs uppercase tracking-[0.25em] text-white/85 hover:text-white">{l}</Link>
    ))}
    <a href="https://www.instagram.com/youth_mgmt/" target="_blank" rel="noopener noreferrer" data-testid="hero-instagram"
      className="link-underline link-light flex w-fit items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-white/85 hover:text-white">
      Instagram <ArrowUpRight className="h-3.5 w-3.5 text-[#e9458f]" />
    </a>
  </nav>
);

const BigLinks = () => (
  <nav data-testid="hero-big-links" className="font-display uppercase leading-[0.9] text-white">
    {BIG_LINKS.map((l, i) => (
      <Link key={l.to} to={l.to} data-testid={`hero-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
        className="group flex w-fit items-center gap-4">
        <MaskLines as="span" lines={[l.label]} delay={0.5 + i * 0.12}
          className="whitespace-nowrap text-[15vw] sm:text-7xl md:text-8xl lg:text-[8.5rem]" />
        <ArrowUpRight className="h-10 w-10 -translate-x-3 text-[#e9458f] opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100 md:h-14 md:w-14" />
      </Link>
    ))}
  </nav>
);

export const EliteHero = ({ models }) => {
  const slides = HERO_SLIDES.map(([slug, n, pos]) => {
    const m = models.find((x) => x.slug === slug);
    return m && { slug, name: m.name, pos, cover: `/media/models/${slug}/${n}.jpg` };
  }).filter(Boolean);
  const [i, setI] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const bigY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length]);

  const current = slides[i];

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1c1418]">
      <motion.div style={{ scale, y }} className="absolute inset-0">
        <AnimatePresence>
          {current && (
            <motion.img key={current.slug} src={current.cover} alt={current.name}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.6 }}
              style={{ objectPosition: current.pos }}
              className="absolute inset-0 h-full w-full object-cover" />
          )}
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-transparent to-black/40" />

      <motion.div style={{ y: bigY }} data-testid="hero-giant-y"
        className="pointer-events-none absolute left-0 top-[3%] w-[41vw] opacity-[0.82] sm:w-[49vw] md:w-[35vw] lg:w-[31vw]">
        <motion.div initial={{ opacity: 0, scale: 0.7, rotate: -10 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}>
          <motion.div key={`y-${i}`} animate={{ rotate: [0, -6, 2, 0], scale: [1, 0.92, 1.03, 1], x: ["0%", "-3%", "1%", "0%"] }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}>
            <YMark className="h-auto w-full drop-shadow-[0_20px_60px_rgba(0,0,0,0.25)]" />
          </motion.div>
        </motion.div>
      </motion.div>

      <div className="relative z-10 flex h-full flex-col justify-between px-5 pb-8 pt-6 md:px-10 md:pb-10 md:pt-8">
        <div className="flex items-start justify-between gap-6">
          <p className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.25em] text-white" data-testid="hero-city">
            <span className="h-2.5 w-2.5 rounded-full bg-[#e9458f]" /> Milano
          </p>
          <div className="flex items-start gap-8 md:gap-12">
            <Link to="/" data-testid="hero-logo" className="flex flex-col items-end">
              <span className="font-display text-5xl uppercase leading-[0.8] text-white md:text-7xl">Youth</span>
              <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/85">Management</span>
            </Link>
            <SideNav />
          </div>
        </div>

        <BigLinks />

        <div className="flex items-end justify-between gap-6">
          <address className="not-italic font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-white/85" data-testid="hero-address">
            Youth Management Milano<br />Via Tortona 16<br />20144 Milano · Italy
          </address>
          {current && (
            <Link to={`/models/${current.slug}`} data-testid="hero-current-model" className="group text-right text-white">
              <span className="block font-mono text-[11px] tracking-[0.25em] text-white/70">
                {String(i + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
              </span>
              <span className="mt-1 flex items-center justify-end gap-2 whitespace-nowrap font-display text-2xl uppercase md:text-3xl">
                {current.name} <ArrowUpRight className="h-5 w-5 text-[#e9458f] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
